import { formattaDataOra } from './formatoData';

// Vocabulary of the iot-temperature domain (CONTEXT.md):
// - Assegnazione = the Sonda-Attrezzatura-Controllo link, with its detection
//   mode and its suspension state. It lives in BSFOOD and is the source of truth.
// - Sospensione = the association is paused, the Controllo goes back to manual,
//   the association and its identity survive. Reversible.
// - Disassociazione = the association is removed, with an immutable trace.
// The two must never be labelled alike in the UI, or the wrong one gets used
// (ADR-0010).

// Etichette dei bottoni di riga. Corte: nella colonna il verbo da solo separa
// gia' le tre azioni, che e' quanto chiedono ADR-0010 e CONTEXT.md — etichette
// *diverse*, non lunghe. Alla lunghezza piena non ci stavano: il testo usciva dal
// bordo del bottone e veniva tagliato al bordo della colonna.
export const ETICHETTA_SOSPENDI = 'Sospendi';
export const ETICHETTA_RIATTIVA = 'Riattiva';
export const ETICHETTA_DISASSOCIA = 'Disassocia';

// Titoli delle conferme. Qui lo spazio c'e' e l'azione si nomina per intero, con
// le parole con cui ADR-0010 la chiama e con cui l'operatore la trova sull'app.
export const TITOLO_SOSPENDI = 'Sospendi il monitoraggio';
export const TITOLO_RIATTIVA = 'Riattiva il monitoraggio';
export const TITOLO_MODIFICA = 'Modifica assegnazione';

// Detection mode lives on the Assegnazione. MVP2 only allows "singola";
// multi-punto is modelled but off (ADR-0005). The values are the names of
// bs-food's `ModalitaRilevazione` enum, which is what the server sends.
export const MODALITA_SINGOLA = 'SINGOLA';
export const MODALITA_MULTI_PUNTO = 'MULTIPUNTO';

// Rete di sicurezza, non il testo di prima scelta: il motivo lo scrive il server, che sa
// *quale* delle condizioni manca (RegoleDiAssegnabilita). Questa frase le mette insieme con
// un «o» e resta solo per la risposta che non ne portasse nessuno.
const MOTIVO_NON_ASSEGNABILE =
  'Non assegnabile: al controllo mancano la grandezza misurata o i limiti completi';

// La rotta di elenco risponde un array nudo; le chiavi restano per il caso in cui
// venga paginata, che e' l'unico impaginamento che il pannello potrebbe ricevere
// senza cambiare schermata.
const estraiLista = (payload, chiavi) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const chiave = chiavi.find((nome) => Array.isArray(payload[nome]));
  return chiave ? payload[chiave] : [];
};

/**
 * La forma della risposta e' quella di `IotAssegnazioneVistaDTO`: campi piatti, e
 * un array nudo. Il lettore resta perche' da' al resto della schermata dei default
 * su cui contare — mai `undefined` in una cella — non perche' indovini fra piu'
 * forme possibili: i rami per entita' annidate leggevano campi che nessuno manda,
 * e le fixture che li inventavano nascondevano proprio questo.
 */
export const normalizzaAssegnazioni = (payload) =>
  estraiLista(payload, ['assegnazioni', 'content', 'items']).map(
    (assegnazione) => ({
      id: assegnazione.id,
      sn: assegnazione.sn ?? '',
      macAddress: assegnazione.macAddress ?? null,
      attrezzaturaId: assegnazione.attrezzaturaId ?? null,
      attrezzaturaNome: assegnazione.attrezzaturaNome ?? '',
      repartoId: assegnazione.repartoId ?? null,
      repartoNome: assegnazione.repartoNome ?? '',
      controlloId: assegnazione.controlloId ?? null,
      controlloNome: assegnazione.controlloNome ?? '',
      modalita: assegnazione.modalita ?? MODALITA_SINGOLA,
      sospesoDal: assegnazione.sospesoDal ?? null,
      statoSonda: assegnazione.statoSonda ?? null,
      ultimaTrasmissione: assegnazione.ultimaTrasmissione ?? null,
    }),
  );

export const isSospesa = (assegnazione) => Boolean(assegnazione?.sospesoDal);

export const STATO_SONDA_TRASMETTE = 'TRASMETTE';
export const STATO_SONDA_SILENTE = 'SILENTE';
export const STATO_SONDA_MAI_VISTA = 'MAI_VISTA';

export const descriviStatoSonda = (assegnazione) => {
  switch (assegnazione?.statoSonda) {
    case STATO_SONDA_TRASMETTE:
      return 'In trasmissione';
    case STATO_SONDA_SILENTE:
      return 'Sonda offline';
    case STATO_SONDA_MAI_VISTA:
      return 'Nessun dato';
    default:
      return 'Nessuna informazione';
  }
};

export const dettaglioStatoSonda = (assegnazione) => {
  if (assegnazione?.statoSonda === STATO_SONDA_MAI_VISTA) {
    return 'Nessuna lettura negli ultimi 7 giorni';
  }
  if (!assegnazione?.statoSonda) {
    return 'Stato della sonda non disponibile al momento';
  }
  const istante = formattaDataOra(assegnazione.ultimaTrasmissione);
  return istante
    ? `Ultima ricezione: ${istante}`
    : 'Ultima ricezione sconosciuta';
};

export const descriviModalita = (modalita) =>
  modalita === MODALITA_MULTI_PUNTO ? 'Multi-punto' : 'Singola';

/**
 * Contract pinned by BSDEVPLAT-94. A non-assignable Controllo is kept in the
 * list with its reason instead of disappearing, so the technician can see what
 * the manual is missing.
 */
export const normalizzaControlliAssegnabili = (payload) => ({
  controlli: estraiLista(payload, ['controlli']).map((controllo) => ({
    id: controllo.id,
    nome: controllo.nome ?? '',
    assegnabile: Boolean(controllo.assegnabile),
    sondaSn: controllo.sondaSn ?? null,
    // Il motivo del server per primo. Riscriverlo con una frase fissa buttava via l'unica
    // cosa che dice al tecnico cosa andare a sistemare — «di categoria PULIZIA_SANIFICAZIONI,
    // che non misura una grandezza» diventava «mancano la grandezza misurata o i Limiti
    // completi», che nomina due cose per non dirne nessuna.
    //
    // Il ramo `sondaSn` resta davanti e resta del pannello: per un Controllo gia' alimentato
    // il server non manda nessun motivo — `sondaSn` e' gia' la risposta — e una seconda
    // formulazione dello stesso fatto e' la deriva che quel contratto evita apposta.
    motivo: controllo.assegnabile
      ? null
      : controllo.sondaSn
        ? `Già alimentato dalla sonda ${controllo.sondaSn}`
        : (controllo.motivo ?? MOTIVO_NON_ASSEGNABILE),
  })),
  messaggioSondaGiaAssegnata: payload?.sondaGiaAssegnata?.messaggio ?? null,
});

/**
 * The negative half of the screen: equipment whose assignable Controlli no sonda
 * is feeding right now. Two situations, and the set is deliberately their union -
 * no Assegnazione at all, or a suspended one, which exists with its identity
 * intact (ADR-0010) while nothing is watching the equipment and the Controllo is
 * back to manual. «Senza Assegnazione» is therefore not what this answers, and
 * the block that renders it must not say so either.
 *
 * «No probe» is an inference here and not a reading: the set is built from the
 * Assegnazioni alone, and the registry (`iot_device`) is never read by attrezzatura
 * on this screen. It holds because `Disassocia` deletes the registry row together
 * with the Assegnazione; where the two archives diverge anyway — a half-done
 * association, a registration made by bs-safe-app — an attrezzatura listed here can
 * still refuse the next sonda with ERR-004.
 */
export const attrezzatureSenzaSonda = ({
  attrezzature = [],
  assegnazioni = [],
  controlliPerAttrezzatura = {},
}) => {
  const attrezzatureSorvegliate = new Set(
    assegnazioni
      .filter((assegnazione) => !isSospesa(assegnazione))
      .map((assegnazione) => String(assegnazione.attrezzaturaId)),
  );

  // Durante una Sospensione l'Assegnazione e la sua identita' sopravvivono
  // (ADR-0010), quindi il server puo' legittimamente rispondere
  // `assegnabile: false` per il Controllo che quella riga alimenta. Senza questa
  // riga il frigo sospeso sparirebbe dal negativo, cioe' proprio il caso che
  // ADR-0010 esiste per rendere visibile. Il confronto e' sull'id del Controllo:
  // la forma della chiave della sonda non e' un'identita' su cui il client possa
  // ragionare (ADR-0013).
  const controlliSospesiPerAttrezzatura = assegnazioni
    .filter(isSospesa)
    .reduce((mappa, assegnazione) => {
      const chiave = String(assegnazione.attrezzaturaId);
      const gia = mappa[chiave] ?? new Set();
      gia.add(String(assegnazione.controlloId));
      mappa[chiave] = gia;
      return mappa;
    }, {});

  return attrezzature
    .filter(
      (attrezzatura) => !attrezzatureSorvegliate.has(String(attrezzatura.id)),
    )
    .map((attrezzatura) => {
      const controlliSospesi =
        controlliSospesiPerAttrezzatura[String(attrezzatura.id)] ?? new Set();

      return {
        attrezzaturaId: attrezzatura.id,
        attrezzaturaNome: attrezzatura.nome ?? '',
        // `nomeReparto`, non `repartoNome`: e' il nome che
        // `AttrezzaturaGestioneOperativaDTO` porta. Il reparto arriva con
        // l'attrezzatura, quindi non serve ricostruirlo dai locali.
        repartoNome: attrezzatura.nomeReparto ?? '',
        controlliAssegnabili: (controlliPerAttrezzatura[attrezzatura.id] ?? [])
          .filter(
            (controllo) =>
              controllo.assegnabile ||
              controlliSospesi.has(String(controllo.id)),
          )
          .map((controllo) => controllo.nome),
      };
    })
    .filter((riga) => riga.controlliAssegnabili.length > 0);
};

/**
 * Conditional write guard. The panel is not the only writer of these rows:
 * bs-safe-app suspends, reactivates and disassociates the same Assegnazioni from
 * the shop floor, and the publication flow remaps them (ADR-0008). The routes
 * carry no version or ETag, so the panel re-reads the list right before acting
 * and refuses to write when the row it is about to touch is no longer the row
 * the technician saw.
 */
export const esitoConfrontoAssegnazione = (assegnazione, elencoAggiornato) => {
  const attuale = (elencoAggiornato ?? []).find(
    (riga) => String(riga.id) === String(assegnazione.id),
  );

  if (!attuale) {
    return {
      invariata: false,
      messaggio:
        "L'assegnazione non esiste più: è stata disassociata da un altro operatore. Elenco aggiornato.",
    };
  }

  if (isSospesa(attuale) !== isSospesa(assegnazione)) {
    return {
      invariata: false,
      messaggio:
        "Lo stato di sospensione dell'assegnazione è cambiato da un'altra postazione. Elenco aggiornato.",
    };
  }

  if (
    String(attuale.controlloId) !== String(assegnazione.controlloId) ||
    String(attuale.sn) !== String(assegnazione.sn)
  ) {
    return {
      invariata: false,
      messaggio:
        "L'assegnazione è stata modificata da un'altra postazione. Elenco aggiornato.",
    };
  }

  return { invariata: true, messaggio: null };
};
